<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  getCurrentInstance,
} from 'vue';
import { sortBy, uniqBy } from 'lodash';
import usuarios from '@/api/usuarios';
import InputFechaHora from '@/components/cifra/InputFechaHora.vue';
import guardias from '../../../api/cifra/guardias/guardias';
import { showApiErrors } from '../../../libs/cifra/flash';
import { dateToTimeString, onlyISODate } from '../../../libs/dates';

const REGEX_HORA_SIN_CERO = /^\d:\d\d/;

// Define emits
const emit = defineEmits(['cancelado', 'guardado']);

// Get current instance for accessing $I18n and $toast
const instance = getCurrentInstance();
const { $I18n, $toast } = instance.appContext.config.globalProperties;

// Methods from usuarios (destructured)
const { fullname, ordenar: ordenarUsuarios } = usuarios;

// Utility functions
const asegurarHora = (hora) => {
  if (hora?.match(REGEX_HORA_SIN_CERO)) {
    return `0${hora}`;
  }
  return hora;
};

// Reactive data
const fecha = ref(onlyISODate(new Date()));
const profesorId = ref(null);
const cargando = ref(false);
const guardando = ref(false);
const infoHorarios = ref({
  horarios: [],
});
const dataHorariosDisponibles = ref([]);
const todosProfesores = ref([]);

const tiposGuardia = ref([
  { name: $I18n.t('ordinaria'), value: 'ordinaria' },
  { name: $I18n.t('Extraordinaria'), value: 'extraordinaria' },
]);

const causasGuardia = ref([
  { name: $I18n.t('lectiva'), value: 'lectiva' },
  { name: $I18n.t('personal'), value: 'personal' },
]);

// Methods
const getCausaByVal = (value) => causasGuardia.value.find((item) => item.value === value).name;

const getTipoByVal = (value) => tiposGuardia.value.find((item) => item.value === value).name;

const profesoresParaGuardias = (horarioData) => {
  const ids = horarioData.usuarios.map((g) => g.id);
  const otros = todosProfesores.value.filter((profe) => (!ids.includes(profe.id)));
  return [
    { grupo: 'Disponibles', usuarios: ordenarUsuarios(horarioData.usuarios) },
    { grupo: 'Otros', usuarios: otros },
  ];
};

const asociarValoresDefecto = (horario) => {
  const [tipoGuardia] = tiposGuardia.value;
  const [causaGuardia] = causasGuardia.value;
  horario.tipoGuardia = tipoGuardia;
  horario.causaGuardia = causaGuardia;
};

const cargarInfoHorario = () => {
  cargando.value = true;
  guardias.infoHorarios(fecha.value).then((result) => {
    infoHorarios.value = result.data;
    infoHorarios.value.horarios.forEach((ih) => {
      asociarValoresDefecto(ih);
      if (ih.horario?.hora_inicio) {
        ih.horario.hora_inicio = asegurarHora(ih.horario.hora_inicio);
        ih.horario.hora_fin = asegurarHora(ih.horario.hora_fin);
      }
    });

    cargando.value = false;
  }).catch((result) => {
    cargando.value = false;
    showApiErrors(result);
  });
};

// Estos son todos los profes, tengan disponibilidad o no
const cargarProfesoresGenerales = () => {
  usuarios.list({ params: { profesor: true, activo: true, baja: false } }).then((result) => {
    todosProfesores.value = result.data;
  });
};

const reset = () => {
  fecha.value = null;
  profesorId.value = null;
};

const guardar = () => {
  guardando.value = true;
  const guardiasGuardar = dataHorariosDisponibles.value.filter((g) => !!g.usuarioSeleccionadoId);

  guardias.save({
    fecha: fecha.value,
    profesor_id: profesorId.value,
    guardias: guardiasGuardar.map((guardia) => (
      {
        horario_id: guardia.horario?.id,
        evento_id: guardia.evento?.id,
        usuario_id: guardia.usuarioSeleccionadoId,
        tipo_guardia: guardia.tipoGuardia?.value,
        causa_guardia: guardia.causaGuardia?.value,
      }
    )),
  }).then((result) => {
    guardando.value = false;
    $toast.success($I18n.t('guardado_correcto'));
    reset();
    emit('guardado', result);
  }).catch((result) => {
    guardando.value = false;
    showApiErrors(result);
  });
};

// Tenemos almacenadas las guardias de cada profe en numero_guardias,
// asignamos el valor al contador del selector para que lo muestre
const _asignarContadoresGuardias = (options) => {
  if (!infoHorarios.value.numero_guardias) return;

  options.forEach((usuario) => {
    usuario.contador = infoHorarios.value.numero_guardias[usuario.id];
    if (usuario.contador) {
      usuario.tooltipContador = $I18n.t('n_guardias_asignadas', { count: usuario.contador });
    }
  });
};

// Computed properties
const profesores = computed(() => ordenarUsuarios(
  infoHorarios.value.horarios.map((ih) => ih.usuario),
));

const profesorSeleccionado = computed(() => (
  profesores.value.find((p) => p.id === profesorId.value)
));
// Son horarios y eventos individuales
const horariosIndividialesProfesorActual = computed(() => (
  infoHorarios.value.horarios.filter((ih) => (
    ih.usuario.id === profesorId.value && ih.horario?.tipo !== 'desdoble'
  ))
));
/**
 * De los horarios de desdoble del profesor actual, nos quedamos con uno solo por cuaderno
 * (si tiene varios horarios de desdoble en el mismo cuaderno, solo mostramos uno)
 */
const horariosDesdoblesProfesorActual = computed(() => {
  const horarios = infoHorarios.value.horarios.filter((ih) => (
    ih.usuario.id === profesorId.value && ih.horario?.tipo === 'desdoble'
  ));
  // Solo ponemos uno por cada sesión (ej miércoles de 10 a 11 de Mates 1º ESO)
  return uniqBy(horarios, (ih) => [ih.cuaderno?.id, ih.horario.dia, ih.horario.hora_inicio].join('-'));
});

const puedeGuardar = computed(() => profesorId.value && fecha.value);

// Watchers
watch(fecha, () => {
  if (fecha.value) {
    cargarInfoHorario();
  }
  profesorId.value = null;
});

watch(profesorId, () => {
  const horarios = horariosIndividialesProfesorActual.value.concat(
    horariosDesdoblesProfesorActual.value,
  ).map((ho) => (
    {
      ...ho,
      hora_inicio: ho.horario?.hora_inicio || ho.evento?.hora_inicio,
    }
  ));
  dataHorariosDisponibles.value = sortBy(horarios, 'hora_inicio');
});

// Lifecycle
onMounted(() => {
  cargarProfesoresGenerales();
  if (fecha.value) {
    cargarInfoHorario();
  }
});
</script>
<template>
  <div class="form-guardia">
    <div class="row">
      <div class="col-md-3">
        <div class="form-group">
          <label for="" class="control-label">{{ $I18n.t('Fecha') }}</label>
          <InputFechaHora v-model="fecha" :disabled="cargando" />
        </div>
      </div>
      <div class="col-md-9">
        <div v-if="!cargando && profesores.length" class="row form-group">
          <div class="col-sm-12">
            <label for="" class="control-label">{{ $I18n.t('profesor_a_cubrir') }}</label>
            <selector-usuario
              v-model="profesorId"
              :usuarios-fijos="profesores"
              tipo="profesor"
            />
          </div>
        </div>
      </div>
    </div>
    <spinner v-if="cargando" />
    <transition enter-active-class="animated fadeInDown">
      <div v-if="profesorSeleccionado" class="horarios">
        <div class="subtitulo-seccion">
          {{ $I18n.t('Horarios') }}
        </div>
        <table class="table">
          <thead>
            <tr>
              <th>{{ $I18n.t('sesiones') }}</th>
              <th class="columna-profesor text-center">
                {{ $I18n.t('Profesor') }}
              </th>
              <th>{{ $I18n.t('Tipo') }}</th>
              <th>{{ $I18n.t('causa') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="horarioData in dataHorariosDisponibles"
              :key="horarioData.horario?.id || horarioData.evento?.id"
            >
              <td>
                <template v-if="horarioData.horario?.tipo === 'individual'">
                  <span class="label label-default">
                    {{ horarioData.horario.hora_inicio }} - {{ horarioData.horario.hora_fin }}
                  </span>
                  <strong class="m-l-sm">
                    {{ horarioData.horario.grupo.etiqueta }}
                  </strong>
                  <div class="m-t-xs">
                    {{ horarioData.horario.asignatura.nombre }}
                  </div>
                </template>
                <template v-if="horarioData.horario?.tipo === 'desdoble'">
                  <span class="label label-default">
                    {{ horarioData.horario.hora_inicio }} - {{ horarioData.horario.hora_fin }}
                  </span>
                  <div class="m-t-xs">
                    <strong>
                      {{ horarioData.cuaderno.nombre }}
                    </strong>
                  </div>
                </template>
                <template v-else-if="horarioData.evento">
                  <span class="m-r-xs" data-toggle="tooltip" :title="$I18n.t('Evento')">
                    <i class="fa fa-calendar-day"></i>
                  </span>
                  <span
                    v-if="horarioData.evento.evento_tipo.hora_lectiva"
                    class="m-r-xs"
                    data-toggle="tooltip"
                    :title="$I18n.t('hora_lectiva')"
                  >
                    <i class="fa fa-book"></i>
                  </span>
                  <span class="label label-default">
                    {{ dateToTimeString(horarioData.evento.inicio) }} -
                    {{ dateToTimeString(horarioData.evento.fin) }}
                  </span>
                  <strong class="m-l-sm">
                    {{ horarioData.evento.grupo.etiqueta }}
                  </strong>
                  <div class="m-t-xs">
                    {{ horarioData.evento.nombre }}
                  </div>
                </template>
              </td>
              <td>
                <div v-if="horarioData.guardia" class="text-center text-info">
                  {{ fullname(horarioData.guardia.usuario) }}
                </div>
                <selector-usuario
                  v-else
                  v-model="horarioData.usuarioSeleccionadoId"
                  :usuarios-fijos="profesoresParaGuardias(horarioData)"
                  tipo="profesor"
                  group-values="usuarios"
                  group-label="grupo"
                  :group-select="false"
                  @usuarios-preparados="_asignarContadoresGuardias"
                />
              </td>
              <td>
                <div v-if="horarioData.guardia" class="text-info">
                  {{ getTipoByVal(horarioData.guardia.tipo) }}
                </div>
                <div v-else>
                  <select-cifra
                    v-if="!horarioData.guardia"
                    v-model="horarioData.tipoGuardia"
                    :options="tiposGuardia"
                    :allow-empty="false"
                    :searchable="false"
                    label="name"
                    track-by="value"
                    :show-labels="false"
                  />
                </div>
              </td>
              <td>
                <div v-if="horarioData.guardia" class="text-info">
                  {{ getCausaByVal(horarioData.guardia.causa) }}
                </div>
                <div v-else>
                  <select-cifra
                    v-if="!horarioData.guardia"
                    v-model="horarioData.causaGuardia"
                    :options="causasGuardia"
                    :allow-empty="false"
                    :searchable="false"
                    label="name"
                    track-by="value"
                    :show-labels="false"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </transition>
    <div class="form-actions">
      <button class="btn btn-default" @click.prevent="$emit('cancelado')">
        {{ $I18n.t('Cancelar') }}
      </button>
      <boton-guardar
        :submitting="guardando"
        :disabled="!puedeGuardar"
        @click="guardar"
      />
    </div>
  </div>
</template>

<style scoped>
  .columna-profesor {
    width: 50%;
  }
</style>
